import { useState } from "react";
import { apiFetch } from "../api";
import { theme } from "../theme";
import RankioLogo from "../components/RankioLogo";

// ── Onboarding Modal ───────────────────────────────────────────────────────────
export default function OnboardingModal({ token, license, onComplete }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [shops, setShops] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    if (!url.trim()) { setError("Vložte URL produktu z Heureky."); return; }
    if (!url.includes("heureka.cz")) { setError("URL musí být z heureka.cz"); return; }
    setLoading(true); setError(""); setShops(null); setSelected(null);
    try {
      const product = await apiFetch("/products/preview?url=" + encodeURIComponent(url.trim()), token);
      if (!product.shops || product.shops.length === 0) {
        setError("U tohoto produktu nebyly nalezeny žádné shopy.");
      } else {
        setShops(product.shops);
      }
    } catch(e) { setError("Chyba: " + e.message); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true); setError("");
    try {
      await apiFetch("/me/settings", token, {
        method: "PATCH",
        body: JSON.stringify({ shop_id: String(selected.id) })
      });
      // Automaticky přidej produkt po nastavení shop_id
      if (url.trim() && url.includes("heureka.cz")) {
        try {
          await apiFetch("/products", token, {
            method: "POST",
            body: JSON.stringify({ heureka_url: url.trim() })
          });
        } catch(e) { /* produkt uz existuje nebo jina chyba - nevadi */ }
      }
      onComplete(String(selected.id));
    } catch(e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: `linear-gradient(135deg, ${theme.ink} 0%, #1e3a8a 55%, #1e40af 100%)`, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 24, fontFamily: theme.font, overflowY: "auto" }}>
      <div style={{ background: theme.bg, borderRadius: theme.rCard, width: "100%", maxWidth: 500, padding: "40px 36px", boxShadow: "0 32px 100px #00000040", margin: "auto" }}>
        <div style={{ marginBottom: 20, display: "flex", justifyContent: "center" }}><RankioLogo height={38} /></div>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: theme.ink, letterSpacing: "-0.5px", marginBottom: 8 }}>Vítejte v Rankio!</h2>
          <p style={{ color: theme.inkSoft, fontSize: 14, lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
            Vložte URL jakéhokoliv vašeho produktu z Heureky — my automaticky rozpoznáme váš e-shop.
          </p>
        </div>

        <div style={{ background: theme.blueSoft, border: `1.5px solid ${theme.blueLine}`, borderRadius: theme.rCard, padding: "10px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🎁</span>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: theme.blueDark }}>Trial aktivní — <span style={{ fontVariantNumeric: theme.tnum }}>14</span> dní zdarma</p>
            <p style={{ fontSize: 12, color: theme.blueDark }}>Plán: {license?.plan || "trial"} · <span style={{ fontVariantNumeric: theme.tnum }}>{license?.days_remaining || 14}</span> dní zbývá</p>
          </div>
        </div>

        <div style={{ background: "#eff6ff", border: `1px solid ${theme.blueLine}`, borderRadius: theme.rCard, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: "#1e40af", lineHeight: 1.6 }}>
          <strong>Jak na to:</strong> Jděte na <a href="https://heureka.cz" target="_blank" rel="noreferrer" style={{ color: "#1e40af", fontWeight: 700 }}>heureka.cz</a>, najděte jakýkoliv produkt který prodáváte, zkopírujte URL a vložte ji níže.
        </div>

        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: theme.inkSoft, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>URL produktu z Heureky</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input
            value={url}
            onChange={e => { setUrl(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="https://www.heureka.cz/auto-moto/..."
            style={{ flex: 1, minWidth: 0, border: "1.5px solid " + (error ? theme.badLine : theme.line), borderRadius: 10, padding: "11px 14px", fontSize: 13, fontFamily: theme.font, color: theme.ink, background: theme.bgSoft }}
            onFocus={e => e.target.style.borderColor = theme.blue}
            onBlur={e => e.target.style.borderColor = error ? theme.badLine : theme.line}
          />
          <button onClick={handleSearch} disabled={loading} style={{ background: theme.ink, border: "none", borderRadius: theme.rBtn, padding: "11px 16px", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
            {loading ? "…" : "Najít"}
          </button>
        </div>

        {error && <p style={{ color: theme.bad, fontSize: 13, marginBottom: 12 }}>{error}</p>}

        {shops && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: theme.mut, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Klikněte na váš e-shop:</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
              {shops.map(shop => (
                <div key={shop.id} onClick={() => setSelected(shop)} style={{ border: "1.5px solid " + (selected?.id === shop.id ? theme.blue : theme.line), borderRadius: 10, padding: "10px 14px", cursor: "pointer", background: selected?.id === shop.id ? theme.blueSoft : theme.bgSoft, display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all 0.15s" }}>
                  <p style={{ fontWeight: 600, fontSize: 14, color: selected?.id === shop.id ? theme.blue : theme.ink }}>{shop.name}</p>
                  {selected?.id === shop.id && <span style={{ color: theme.blue, fontSize: 16 }}>✓</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={handleSave} disabled={!selected || saving} style={{ width: "100%", padding: "14px", background: selected ? `linear-gradient(135deg, ${theme.blue}, ${theme.blueDark})` : theme.line, border: "none", borderRadius: theme.rBtn, color: selected ? "#fff" : theme.mut, fontSize: 16, fontWeight: 700, cursor: selected ? "pointer" : "not-allowed", fontFamily: theme.font, marginBottom: 12, boxShadow: selected ? "0 4px 20px #126DFB40" : "none" }}>
          {saving ? "Ukládám…" : selected ? "Potvrdit — " + selected.name : "Nejdřív vyberte váš e-shop"}
        </button>
        <button onClick={() => onComplete(null)} style={{ width: "100%", padding: "11px", background: "none", border: `1.5px solid ${theme.line}`, borderRadius: theme.rBtn, color: theme.mut, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: theme.font }}>
          Přeskočit — nastavím později
        </button>
      </div>
    </div>
  );
}
